<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SettingsBackButton from 'dashboard/components-next/Settings/SettingsBackButton.vue';
import { getChannelSvgUrlExact } from 'dashboard/components-next/icon/channelSvgUrls';
import { useChannelConnectDefinitions } from 'dashboard/composables/useChannelConnectDefinitions';
import { useAccount } from 'dashboard/composables/useAccount';
import { getCatalogCardKeyForInbox } from 'dashboard/helper/inboxCatalogCardStyle';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { accountId } = useAccount();

const channelDefinitions = useChannelConnectDefinitions();

const flowInbox = computed(() => {
  const id = route.params.inbox_id;
  if (id == null || id === '') return null;
  return store.getters['inboxes/getInbox'](id) ?? null;
});

const activeConnectKey = computed(() => {
  if (route.name === 'settings_inboxes_page_channel') {
    return route.params.sub_page ?? null;
  }
  if (route.name === 'settings_inbox_finish') {
    return getCatalogCardKeyForInbox(flowInbox.value) ?? null;
  }
  return null;
});

const channelMeta = computed(() => {
  const key = activeConnectKey.value;
  if (!key) return null;
  return channelDefinitions.value.find(ch => ch.key === key) ?? null;
});

const sidebarSvgUrl = computed(() =>
  getChannelSvgUrlExact(channelMeta.value?.svgSlug ?? null)
);

const flowSidebarTitle = computed(() => {
  if (route.name === 'settings_inbox_finish') {
    return t('INBOX_MGMT.CREATE_FLOW.FINISH.TITLE');
  }
  return t('INBOX_MGMT.CREATE_FLOW.INBOX.TITLE');
});

const flowSidebarDescription = computed(() => {
  if (route.name === 'settings_inbox_finish') {
    return t('INBOX_MGMT.CREATE_FLOW.FINISH.BODY');
  }
  return '';
});

const flowBackUrl = computed(() => {
  const id = accountId.value;
  const channelList = {
    name: 'settings_inbox_list',
    params: { accountId: id },
  };
  const channelCatalog = {
    name: 'settings_inbox_new',
    params: { accountId: id },
  };

  switch (route.name) {
    case 'settings_inboxes_page_channel':
      return channelCatalog;
    case 'settings_inbox_finish':
      return channelList;
    default:
      return channelList;
  }
});

const onFlowBack = () => {
  router.push(flowBackUrl.value);
};
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col bg-n-surface-1">
    <div class="flex min-h-0 flex-1 flex-col md:flex-row">
      <aside
        class="flex w-full shrink-0 flex-col gap-4 border-n-weak px-5 pt-12 pb-6 md:w-52 md:border-e md:px-5 md:pt-14 md:pb-7 lg:px-6"
        :aria-label="$t('INBOX_MGMT.CHANNEL_CATALOG.TITLE')"
      >
        <template v-if="channelMeta">
          <div class="flex w-full shrink-0 justify-center">
            <img
              v-if="sidebarSvgUrl"
              :src="sidebarSvgUrl"
              alt=""
              class="size-32 shrink-0 object-contain"
              decoding="async"
              draggable="false"
            />
            <Icon
              v-else
              :icon="channelMeta.icon"
              class="size-32 shrink-0 text-n-slate-10"
            />
          </div>
          <h2 class="mb-0 text-start text-xl font-bold text-n-slate-12">
            {{ channelMeta.title }}
          </h2>
          <p class="mb-0 text-start text-sm leading-relaxed text-n-slate-11">
            {{ channelMeta.description }}
          </p>
        </template>

        <template v-else>
          <div class="flex w-full shrink-0 justify-center">
            <Icon icon="i-lucide-sparkles" class="size-32 text-n-slate-10" />
          </div>
          <h2 class="mb-0 text-start text-xl font-bold text-n-slate-12">
            {{ flowSidebarTitle }}
          </h2>
          <p class="mb-0 text-start text-sm leading-relaxed text-n-slate-11">
            {{ flowSidebarDescription }}
          </p>
        </template>
      </aside>

      <main
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-n-surface-1 md:bg-n-solid-1"
      >
        <div class="flex shrink-0 justify-start px-4 py-3 md:px-6 md:py-4">
          <SettingsBackButton
            :label="$t('GENERAL_SETTINGS.BACK')"
            @click="onFlowBack"
          />
        </div>
        <div class="min-h-0 min-w-0 flex-1">
          <router-view v-slot="{ Component }">
            <component :is="Component" :key="route.fullPath" />
          </router-view>
        </div>
      </main>
    </div>
  </div>
</template>
